import React, { memo, ReactElement, useMemo, ChangeEvent } from 'react'
import { TAG_SIZE, Tag, TAG_STYLE } from 'transfix-ui/components/Tag'
import { TagSelect } from 'transfix-ui/components/TagSelect'
import { $enum } from 'ts-enum-util'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import {
  getLoadStatusOptionValue,
  filterPickupStops,
  filterDropoffStops,
  getMarketplaceOptions,
  EDITABLE_MP_STATES,
  getMarketplaceTag,
  getLoadStatusTagStyle,
} from 'apps/fleet-planner/utils/loadStatusTags'
import { ILoadStatusFunctionalTagMarketplaceProps } from './LoadStatusFunctionalTagMarketplace'

interface ILoadStatusFunctionalTagMarketplaceMobilePropsMobile
  extends ILoadStatusFunctionalTagMarketplaceProps {
  /**
   * @default 'medium'
   */
  size?: keyof typeof TAG_SIZE
}

const getNonEditableTag = (status: LOAD_STATE): { label: string; style: TAG_STYLE } => {
  // Delivered can be both an editable and non-editable status
  if (EDITABLE_MP_STATES.includes(status)) return { label: 'Delivered', style: TAG_STYLE.info }
  return getMarketplaceTag(status)
}

export const LoadStatusFunctionalTagMarketplaceMobile = memo<
  ILoadStatusFunctionalTagMarketplaceMobilePropsMobile
>(
  ({
    status,
    currentStopSequence = 0,
    onChange,
    stops = [],
    dataTest = 'load-status-mobile',
    isLoading,
    size = TAG_SIZE.medium,
  }): ReactElement => {
    const { trackEvent } = useFMSAnalytics()
    const currentStatusValue = useMemo(
      () =>
        getLoadStatusOptionValue(
          $enum(LOAD_STATE).getKeyOrDefault(status, 'Ready for Dispatch'),
          currentStopSequence
        ),
      [status, currentStopSequence]
    )

    const pickupStops = useMemo(() => filterPickupStops(stops), [stops])
    const dropoffStops = useMemo(() => filterDropoffStops(stops), [stops])
    const initialOptions = useMemo(() => getMarketplaceOptions({ pickupStops, dropoffStops }), [
      pickupStops,
      dropoffStops,
    ])

    // Hide previous shipment statuses to prevent backwards state transitions.
    const currentStatus = initialOptions.find(
      initialOption => initialOption.value === currentStatusValue
    )
    const currentStatusIndex = currentStatus ? initialOptions.indexOf(currentStatus) : 0
    const options = initialOptions.slice(currentStatusIndex)

    const changeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
      const selectedOption = options.find(e => e?.value === event?.target?.value)
      if (selectedOption) {
        const [, updatedStatus, stopSequence] = selectedOption.value.split(':') as [
          string,
          LOAD_STATE,
          string
        ]
        onChange({ status: updatedStatus, currentStopSequence: stopSequence })
        trackEvent('Load', 'CHANGED_LOAD_STATUS')
      }
    }

    const statusKey = $enum(LOAD_STATE).getKeyOrDefault(status, 'Pending')
    const statusTagStyle = getLoadStatusTagStyle(statusKey)

    const value = options?.find(v => v?.value === currentStatusValue)

    const isLoadStatusEditable = EDITABLE_MP_STATES.includes(status)
    const { label, style } = getNonEditableTag(status)

    return (
      <div className='fp-status-select flex items-center'>
        {isLoadStatusEditable && options.length > 1 ? (
          <TagSelect
            options={options}
            size={TAG_SIZE[size]}
            tagStyle={statusTagStyle}
            outlineOnly={status === LOAD_STATE.Pending}
            onChange={changeHandler}
            value={value}
            disabled={isLoading}
            dataTest={dataTest}
          />
        ) : (
          <Tag label={label} size={TAG_SIZE.small} tagStyle={style} />
        )}
      </div>
    )
  }
)
